import { type CertificationDto, type SearchAnalyticsEventDto, type SkillDto } from "@crowdcoursing/api/openapi";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo, useRef, useState } from "react";

import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";

type ItemLimit = 10 | 25 | 50 | "all";

const LIMIT_STORAGE_KEY = "talentAlignment.itemLimit";

const getInitialLimit = (): ItemLimit => {
  const stored = localStorage.getItem(LIMIT_STORAGE_KEY);
  if (stored === "10" || stored === "25" || stored === "50" || stored === "all") {
    return stored === "all" ? "all" : (parseInt(stored, 10) as ItemLimit);
  }
  return 25;
};

// Aggregate keywords from search data
interface KeywordCount {
  count: number;
  keyword: string;
  type: "skill" | "certification" | "keyword";
}

const aggregateKeywords = (data: SearchAnalyticsEventDto[] | null): KeywordCount[] => {
  if (!data || data.length === 0) {
    return [];
  }

  // Track counts per name per type - same logic as CandidateSearchAnalytics
  const skillCounts: Record<string, number> = {};
  const certCounts: Record<string, number> = {};
  const keywordCounts: Record<string, number> = {};
  const otherKeywords: Record<string, number> = {};

  for (const event of data) {
    if (!event.keywords) {
      continue;
    }

    // Track what we've seen in THIS event
    const seenSkills = new Set<string>();
    const seenCerts = new Set<string>();

    for (const keyword of event.keywords) {
      if (keyword.startsWith("skill:")) {
        seenSkills.add(keyword.substring(6));
      } else if (keyword.startsWith("cert:")) {
        seenCerts.add(keyword.substring(5));
      } else if (keyword.startsWith("certification:")) {
        seenCerts.add(keyword.substring(14));
      } else if (!keyword.match(/^graduationYear[<>]=?\d+$/) && !keyword.match(/^gpaWeighted[<>]=?[\d.]+$/)) {
        // Skip filter expressions (graduationYear, gpaWeighted), count everything else
        otherKeywords[keyword] = (otherKeywords[keyword] || 0) + 1;
      }
    }

    // For each name in this event, determine its type based on THIS search
    const allNamesInEvent = new Set([...seenSkills, ...seenCerts]);

    allNamesInEvent.forEach((name) => {
      const isSkill = seenSkills.has(name);
      const isCert = seenCerts.has(name);

      if (isSkill && isCert) {
        // This specific search has both skill:X and cert:X - it's a keyword search
        keywordCounts[name] = (keywordCounts[name] || 0) + 1;
      } else if (isSkill) {
        skillCounts[name] = (skillCounts[name] || 0) + 1;
      } else if (isCert) {
        certCounts[name] = (certCounts[name] || 0) + 1;
      }
    });
  }

  // Build results array
  const results: KeywordCount[] = [];

  Object.entries(skillCounts).forEach(([keyword, count]) => {
    results.push({ count, keyword, type: "skill" });
  });

  Object.entries(certCounts).forEach(([keyword, count]) => {
    results.push({ count, keyword, type: "certification" });
  });

  Object.entries(keywordCounts).forEach(([keyword, count]) => {
    results.push({ count, keyword, type: "keyword" });
  });

  Object.entries(otherKeywords).forEach(([keyword, count]) => {
    results.push({ count, keyword, type: "keyword" });
  });

  return results.sort((a, b) => b.count - a.count);
};

// Aggregate skills and certifications from platform data
interface PlatformItemCount {
  count: number;
  name: string;
  type: "skill" | "certification";
}

const aggregateSkillsAndCerts = (
  skills: SkillDto[] | null,
  certifications: CertificationDto[] | null,
): PlatformItemCount[] => {
  const itemMap = new Map<string, PlatformItemCount>();

  if (skills) {
    for (const skill of skills) {
      if (skill.name) {
        const key = `skill:${skill.name.toLowerCase()}`;
        const existing = itemMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          itemMap.set(key, { count: 1, name: skill.name, type: "skill" });
        }
      }
    }
  }

  if (certifications) {
    for (const cert of certifications) {
      if (cert.name) {
        const key = `cert:${cert.name.toLowerCase()}`;
        const existing = itemMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          itemMap.set(key, { count: 1, name: cert.name, type: "certification" });
        }
      }
    }
  }

  return Array.from(itemMap.values()).sort((a, b) => b.count - a.count);
};

// Build comparison data between searches and platform items
interface ComparisonRow {
  name: string;
  platformCount: number | null;
  platformRank: number | null;
  searchCount: number;
  searchRank: number;
  type: "skill" | "certification" | "keyword";
}

const buildComparisonData = (
  searchData: KeywordCount[],
  platformData: PlatformItemCount[],
  limit: ItemLimit,
): ComparisonRow[] => {
  const limitedSearchData = limit === "all" ? searchData : searchData.slice(0, limit);

  // Create lookup map for platform data ranks
  const platformRankMap = new Map<string, { count: number; rank: number }>();
  platformData.forEach((item, index) => {
    const key = `${item.type}:${item.name.toLowerCase()}`;
    platformRankMap.set(key, { count: item.count, rank: index + 1 });
  });

  return limitedSearchData.map((search, index) => {
    // For keywords that are both skill and cert, check both
    let platformInfo: { count: number; rank: number } | null = null;

    if (search.type === "skill") {
      platformInfo = platformRankMap.get(`skill:${search.keyword.toLowerCase()}`) ?? null;
    } else if (search.type === "certification") {
      platformInfo = platformRankMap.get(`cert:${search.keyword.toLowerCase()}`) ?? null;
    } else {
      // For keyword type, check if it exists as either skill or cert
      platformInfo =
        platformRankMap.get(`skill:${search.keyword.toLowerCase()}`) ??
        platformRankMap.get(`cert:${search.keyword.toLowerCase()}`) ??
        null;
    }

    return {
      name: search.keyword,
      platformCount: platformInfo?.count ?? null,
      platformRank: platformInfo?.rank ?? null,
      searchCount: search.count,
      searchRank: index + 1,
      type: search.type,
    };
  });
};

// Chart components
interface PopularSearchesChartProps {
  data: KeywordCount[];
  limit: ItemLimit;
  loading: boolean;
}

const PopularSearchesChart = ({ data, limit, loading }: PopularSearchesChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const limitedData = limit === "all" ? data : data.slice(0, limit);
  const chartHeight = Math.max(400, limitedData.length * 25);

  const getColorForType = (type: "skill" | "certification" | "keyword"): string => {
    switch (type) {
      case "skill":
        return "#3b82f6"; // Blue
      case "certification":
        return "#8b5cf6"; // Purple
      case "keyword":
        return "#10b981"; // Green
      default:
        return "#6b7280"; // Gray
    }
  };

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        animation: false,
        height: chartHeight,
        type: "bar",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          colorByPoint: true,
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          colors: limitedData.map((d) => getColorForType(d.type)),
          data: limitedData.map((d) => d.count),
          name: "Searches",
          type: "bar",
        },
      ],
      title: {
        text: "Popular Searches",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = limitedData[index];
          const typeLabel =
            item?.type === "skill" ? "Skill" : item?.type === "certification" ? "Certification" : "Keyword";
          return `<b>${item?.keyword}</b><br/>${typeLabel}<br/>Searches: ${point.y}`;
        },
      },
      xAxis: {
        categories: limitedData.map((d) => d.keyword),
        title: {
          text: null,
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [chartHeight, limitedData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (limitedData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#3b82f6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Skills</Typography>
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#8b5cf6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Certifications</Typography>
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#10b981", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Keywords</Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface AvailableSkillsChartProps {
  data: PlatformItemCount[];
  limit: ItemLimit;
  loading: boolean;
}

const getColorForPlatformType = (type: "skill" | "certification"): string => {
  switch (type) {
    case "skill":
      return "#3b82f6"; // Blue (same as search skills)
    case "certification":
      return "#8b5cf6"; // Purple (same as search certs)
    default:
      return "#f97316"; // Orange
  }
};

const AvailableSkillsChart = ({ data, limit, loading }: AvailableSkillsChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const limitedData = limit === "all" ? data : data.slice(0, limit);
  const chartHeight = Math.max(400, limitedData.length * 25);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        animation: false,
        height: chartHeight,
        type: "bar",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          colorByPoint: true,
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          colors: limitedData.map((d) => getColorForPlatformType(d.type)),
          data: limitedData.map((d) => d.count),
          name: "Count",
          type: "bar",
        },
      ],
      title: {
        text: "Skills & Certifications Breakdown",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = limitedData[index];
          const typeLabel = item?.type === "skill" ? "Skill" : "Certification";
          return `<b>${item?.name}</b><br/>${typeLabel}<br/>Candidates: ${point.y}`;
        },
      },
      xAxis: {
        categories: limitedData.map((d) => d.name),
        title: {
          text: null,
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Candidates",
        },
      },
    }),
    [chartHeight, limitedData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (limitedData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography color="text.secondary">No skills data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#3b82f6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Skills</Typography>
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#8b5cf6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Certifications</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export interface TalentAlignmentDashboardProps {
  certifications?: CertificationDto[] | null;
  /**
   * When true, candidate search analytics are not used (e.g. district/school admin until a search API exists for this context).
   * Comparison and “popular searches” stay empty; skills/cert charts still reflect the bubble-scoped resume data passed in.
   */
  omitCandidateSearchAnalytics?: boolean;
  onTimeRangeChange?: (newTimeRange: DateRangeValue) => void;
  searchData: SearchAnalyticsEventDto[] | null;
  searchStatus: "error" | "pending" | "success";
  skills: SkillDto[] | null;
  skillsStatus: "error" | "pending" | "success";
  timeRange?: DateRangeValue;
}

export const TalentAlignmentDashboard = ({
  certifications,
  omitCandidateSearchAnalytics = false,
  onTimeRangeChange,
  searchData,
  searchStatus,
  skills,
  skillsStatus,
  timeRange,
}: TalentAlignmentDashboardProps) => {
  const [itemLimit, setItemLimit] = useState<ItemLimit>(getInitialLimit);

  const handleLimitChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const newLimit: ItemLimit = value === "all" ? "all" : (parseInt(value, 10) as ItemLimit);
    setItemLimit(newLimit);
    localStorage.setItem(LIMIT_STORAGE_KEY, String(newLimit));
  };

  const effectiveSearchData = omitCandidateSearchAnalytics ? null : searchData;
  const searchLoading = !omitCandidateSearchAnalytics && searchStatus === "pending";
  const skillsLoading = skillsStatus === "pending";

  const aggregatedKeywords = useMemo(() => aggregateKeywords(effectiveSearchData), [effectiveSearchData]);
  const aggregatedSkillsAndCerts = useMemo(
    () => aggregateSkillsAndCerts(skills, certifications ?? null),
    [skills, certifications],
  );
  const comparisonData = useMemo(
    () => buildComparisonData(aggregatedKeywords, aggregatedSkillsAndCerts, itemLimit),
    [aggregatedKeywords, aggregatedSkillsAndCerts, itemLimit],
  );

  if (skillsStatus === "error" || (!omitCandidateSearchAnalytics && searchStatus === "error")) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
          <Typography color="error">Error loading data. Please try again.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      {/* Insight section */}
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {omitCandidateSearchAnalytics ? (
            <>
              <strong>Organization view:</strong> Skills and certifications below reflect candidates tied to this
              district or school (including child schools for districts). Platform-wide candidate search trends and the
              search-vs-profile comparison are available under Platform Insights for site administrators.
              <br />
              <br />
            </>
          ) : (
            <>
              <strong>Comparison Table:</strong> Shows the search rank vs. platform rank for each searched item. Items
              highlighted in red are searched for but not found on any candidate profiles.
              <br />
              <strong>Popular Searches:</strong> Skills, certifications, and keywords employers are actively searching
              for.
              <br />
            </>
          )}
          <strong>Skills & Certifications Breakdown:</strong> Skills and certifications that candidates in this view
          have listed on their profiles.
          <br />
          <br />
          {!omitCandidateSearchAnalytics ? (
            <em>
              Look for gaps where a skill is highly searched but not well-represented in the talent pool - these
              represent opportunities for training or recruitment.
            </em>
          ) : null}
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {timeRange && onTimeRangeChange && (
          <Box sx={{ width: 240 }}>
            <PopoverDateRangePicker onChange={onTimeRangeChange} size="small" value={timeRange} />
          </Box>
        )}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Show Top</InputLabel>
          <Select label="Show Top" onChange={handleLimitChange} value={String(itemLimit)}>
            <MenuItem value="10">Top 10</MenuItem>
            <MenuItem value="25">Top 25</MenuItem>
            <MenuItem value="50">Top 50</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Comparison Table */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ mb: 2 }} variant="h6">
          {omitCandidateSearchAnalytics ? "Search comparison" : "Search vs. Platform Comparison"}
        </Typography>
        {searchLoading || skillsLoading ? (
          <Box sx={{ alignItems: "center", display: "flex", height: 200, justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : comparisonData.length === 0 ? (
          <Box sx={{ alignItems: "center", display: "flex", height: 200, justifyContent: "center" }}>
            <Typography color="text.secondary">
              {omitCandidateSearchAnalytics
                ? "Candidate search rankings are not loaded in this view. See skills and certifications below."
                : "No data available for comparison"}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Search Rank</TableCell>
                  <TableCell align="center">Platform Rank</TableCell>
                  <TableCell align="right">Students with Skill</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.map((row) => (
                  <TableRow
                    key={`${row.type}-${row.name}`}
                    sx={{
                      backgroundColor: row.platformRank === null ? "rgba(239, 68, 68, 0.1)" : undefined,
                    }}
                  >
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          alignItems: "center",
                          backgroundColor:
                            row.type === "skill" ? "#3b82f6" : row.type === "certification" ? "#8b5cf6" : "#10b981",
                          borderRadius: 1,
                          color: "white",
                          display: "inline-flex",
                          fontSize: "0.75rem",
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {row.type === "skill" ? "Skill" : row.type === "certification" ? "Cert" : "Keyword"}
                      </Box>
                    </TableCell>
                    <TableCell align="center">#{row.searchRank}</TableCell>
                    <TableCell align="center">
                      {row.platformRank !== null ? (
                        `#${row.platformRank}`
                      ) : (
                        <Typography color="error" component="span" variant="body2">
                          Not found
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{row.platformCount !== null ? row.platformCount : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Side by side charts */}
      <Grid container spacing={3}>
        <Grid size={{ xl: 6, xs: 12 }}>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
            <PopularSearchesChart data={aggregatedKeywords} limit={itemLimit} loading={searchLoading} />
          </Box>
        </Grid>
        <Grid size={{ xl: 6, xs: 12 }}>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
            <AvailableSkillsChart data={aggregatedSkillsAndCerts} limit={itemLimit} loading={skillsLoading} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
